/* ==========================================================================
   Cart Drawer - Averdo Buchhandlung
   Vanilla JS for cart drawer: open/close, quantity updates, remove items,
   cart count badge updates, and custom event handling.
   ========================================================================== */

(function() {
  'use strict';

  /* -----------------------------------------------------------------------
     DOM References
     ----------------------------------------------------------------------- */
  var drawer = document.getElementById('CartDrawer');
  var overlay = document.getElementById('overlay');
  var drawerBody = document.getElementById('CartDrawerBody');
  var drawerItems = document.getElementById('CartDrawerItems');
  var drawerFooter = document.getElementById('CartDrawerFooter');

  if (!drawer) return;

  /* -----------------------------------------------------------------------
     State
     ----------------------------------------------------------------------- */
  var isUpdating = false;

  /* -----------------------------------------------------------------------
     Open / Close
     ----------------------------------------------------------------------- */
  function openDrawer() {
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');

    /* Focus the close button for accessibility */
    var closeBtn = drawer.querySelector('[data-cart-drawer-close]');
    if (closeBtn) closeBtn.focus();

    /* Trap focus inside drawer */
    document.addEventListener('keydown', handleKeyDown);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-active');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');

    document.removeEventListener('keydown', handleKeyDown);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      closeDrawer();
    }

    /* Basic focus trap */
    if (e.key === 'Tab') {
      var focusable = drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* Bind open buttons (any element with data-cart-drawer-open) */
  document.addEventListener('click', function(e) {
    var openBtn = e.target.closest('[data-cart-drawer-open]');
    if (openBtn) {
      e.preventDefault();
      openDrawer();
      return;
    }

    var closeBtn = e.target.closest('[data-cart-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      closeDrawer();
      return;
    }
  });

  /* Close on overlay click */
  if (overlay) {
    overlay.addEventListener('click', function() {
      closeDrawer();
    });
  }

  /* -----------------------------------------------------------------------
     Cart API Helpers
     ----------------------------------------------------------------------- */
  function fetchCartChange(key, quantity) {
    if (isUpdating) return;
    isUpdating = true;

    setLoadingState(true);

    return fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Netzwerkfehler beim Aktualisieren des Warenkorbs');
      }
      return response.json();
    })
    .then(function(cart) {
      /* Dispatch custom event so other components can react */
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));

      /* Re-render the drawer content */
      renderDrawer(cart);

      /* Update all cart count badges across the page */
      updateCartCountBadges(cart.item_count);

      isUpdating = false;
      setLoadingState(false);
    })
    .catch(function(error) {
      console.error('Cart update error:', error);
      isUpdating = false;
      setLoadingState(false);
    });
  }

  function setLoadingState(loading) {
    if (loading) {
      drawer.classList.add('cart-drawer--loading');
    } else {
      drawer.classList.remove('cart-drawer--loading');
    }
  }

  /* -----------------------------------------------------------------------
     Render Drawer Contents
     ----------------------------------------------------------------------- */
  function renderDrawer(cart) {
    var body = document.getElementById('CartDrawerBody');
    var footer = document.getElementById('CartDrawerFooter');

    if (cart.item_count === 0) {
      /* Empty state */
      body.innerHTML =
        '<div class="cart-drawer__empty" id="CartDrawerEmpty">' +
          '<div class="cart-drawer__empty-icon">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>' +
          '</div>' +
          '<p class="cart-drawer__empty-text">Ihr Warenkorb ist leer</p>' +
          '<a href="' + (window.routes.root_url || '/') + 'collections/all" class="btn btn--primary" data-cart-drawer-close>Weiter einkaufen</a>' +
        '</div>';

      if (footer) footer.style.display = 'none';

      /* Update drawer count badge */
      var drawerCount = drawer.querySelector('[data-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = '0';

      return;
    }

    /* Free Shipping Bar */
    var threshold = window.theme && window.theme.freeShippingThreshold ? window.theme.freeShippingThreshold * 100 : 0;
    var shippingBarHtml = '';
    if (threshold > 0) {
      var remaining = threshold - cart.total_price;
      var progress = Math.min(100, Math.round(cart.total_price / threshold * 100));
      var isDone = remaining <= 0;
      shippingBarHtml =
        '<div class="shipping-bar' + (isDone ? ' shipping-bar--done' : '') + '">' +
          '<p class="shipping-bar__text">' +
            (isDone
              ? '\u2713 Du bekommst kostenlosen Versand!'
              : 'Noch <strong>' + formatMoney(remaining) + '</strong> bis zum kostenlosen Versand') +
          '</p>' +
          '<div class="shipping-bar__track"><div class="shipping-bar__fill" style="width:' + progress + '%"></div></div>' +
        '</div>';
    }

    /* Build line items HTML */
    var itemsHtml = '';
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var imageHtml = '';

      if (item.image) {
        imageHtml =
          '<img src="' + getSizedImageUrl(item.image, '180x') + '" ' +
          'alt="' + escapeHtml(item.product_title) + '" ' +
          'width="72" height="108" loading="lazy">';
      }

      var variantHtml = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantHtml = '<span class="cart-drawer-item__variant">' + escapeHtml(item.variant_title) + '</span>';
      }

      var priceHtml = '';
      if (item.original_price !== item.final_price) {
        priceHtml =
          '<span style="text-decoration: line-through; color: var(--color-text-muted); font-weight: 400; font-size: var(--font-size-xs);">' + formatMoney(item.original_price) + '</span> ' +
          formatMoney(item.final_price);
      } else {
        priceHtml = formatMoney(item.original_price);
      }

      itemsHtml +=
        '<div class="cart-drawer-item" data-cart-drawer-item data-key="' + item.key + '" data-line="' + (i + 1) + '">' +
          '<a href="' + item.url + '" class="cart-drawer-item__image" aria-label="' + escapeHtml(item.product_title) + '">' +
            imageHtml +
          '</a>' +
          '<div class="cart-drawer-item__info">' +
            '<a href="' + item.url + '" class="cart-drawer-item__title">' + escapeHtml(item.product_title) + '</a>' +
            variantHtml +
            '<span class="cart-drawer-item__price">' + priceHtml + '</span>' +
            '<div class="cart-drawer-item__actions">' +
              '<div class="quantity-selector">' +
                '<button type="button" class="quantity-selector__btn" aria-label="Menge verringern" data-drawer-quantity-minus data-key="' + item.key + '" data-line="' + (i + 1) + '"' + (item.quantity <= 1 ? ' disabled' : '') + '>&minus;</button>' +
                '<input type="number" value="' + item.quantity + '" min="0" aria-label="Menge" class="quantity-selector__input" data-drawer-quantity-input data-key="' + item.key + '" data-line="' + (i + 1) + '" readonly>' +
                '<button type="button" class="quantity-selector__btn" aria-label="Menge erh\u00f6hen" data-drawer-quantity-plus data-key="' + item.key + '" data-line="' + (i + 1) + '">&plus;</button>' +
              '</div>' +
              '<button type="button" class="cart-drawer-item__remove" data-drawer-remove-item data-key="' + item.key + '" data-line="' + (i + 1) + '" aria-label="Entfernen: ' + escapeHtml(item.product_title) + '">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    }

    body.innerHTML = shippingBarHtml + '<div class="cart-drawer__items" id="CartDrawerItems">' + itemsHtml + '</div>';

    /* Update footer */
    if (footer) {
      footer.style.display = '';
      var subtotalEl = footer.querySelector('[data-cart-drawer-subtotal]');
      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(cart.total_price);
      }
    } else {
      /* Re-create footer if it was removed (empty state -> items added) */
      var footerHtml =
        '<div class="cart-drawer__footer" id="CartDrawerFooter">' +
          '<div class="cart-drawer__shipping-note">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>' +
            '<span>Portofrei innerhalb Deutschlands</span>' +
          '</div>' +
          '<div class="cart-drawer__subtotal">' +
            '<span class="cart-drawer__subtotal-label">Zwischensumme</span>' +
            '<span class="cart-drawer__subtotal-price" data-cart-drawer-subtotal>' + formatMoney(cart.total_price) + '</span>' +
          '</div>' +
          '<div class="cart-drawer__checkout">' +
            '<a href="' + (window.routes.cart_url || '/cart') + '/checkout" class="btn btn--primary btn--full btn--large">Zur Kasse</a>' +
          '</div>' +
          '<a href="' + (window.routes.cart_url || '/cart') + '" class="cart-drawer__continue">Warenkorb ansehen</a>' +
        '</div>';

      drawer.insertAdjacentHTML('beforeend', footerHtml);
    }

    /* Update drawer count badge */
    var drawerCount = drawer.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = cart.item_count;
  }

  /* -----------------------------------------------------------------------
     Event Delegation for Drawer Actions
     ----------------------------------------------------------------------- */
  drawer.addEventListener('click', function(e) {
    var minusBtn = e.target.closest('[data-drawer-quantity-minus]');
    var plusBtn = e.target.closest('[data-drawer-quantity-plus]');
    var removeBtn = e.target.closest('[data-drawer-remove-item]');

    if (minusBtn) {
      e.preventDefault();
      var key = minusBtn.getAttribute('data-key');
      var input = minusBtn.closest('.quantity-selector').querySelector('[data-drawer-quantity-input]');
      var newQty = Math.max(0, parseInt(input.value) - 1);
      fetchCartChange(key, newQty);
    }

    if (plusBtn) {
      e.preventDefault();
      var key = plusBtn.getAttribute('data-key');
      var input = plusBtn.closest('.quantity-selector').querySelector('[data-drawer-quantity-input]');
      var newQty = parseInt(input.value) + 1;
      fetchCartChange(key, newQty);
    }

    if (removeBtn) {
      e.preventDefault();
      var key = removeBtn.getAttribute('data-key');
      fetchCartChange(key, 0);
    }
  });

  /* -----------------------------------------------------------------------
     Update Cart Count Badges
     ----------------------------------------------------------------------- */
  function updateCartCountBadges(count) {
    /* Update all elements with data-cart-count attribute */
    var badges = document.querySelectorAll('[data-cart-count]');
    badges.forEach(function(badge) {
      badge.textContent = count;
      if (count > 0) {
        badge.style.display = '';
        badge.removeAttribute('hidden');
      } else {
        badge.style.display = 'none';
      }
    });

    /* Also update aria-label on cart icon buttons */
    var cartButtons = document.querySelectorAll('[data-cart-drawer-open]');
    cartButtons.forEach(function(btn) {
      btn.setAttribute('aria-label', 'Warenkorb (' + count + ')');
    });
  }

  /* -----------------------------------------------------------------------
     Listen for cart:updated events from other components
     ----------------------------------------------------------------------- */
  document.addEventListener('cart:updated', function(e) {
    var cart = e.detail && e.detail.cart;
    if (cart && !isUpdating) {
      renderDrawer(cart);
      updateCartCountBadges(cart.item_count);
    }
  });

  /* -----------------------------------------------------------------------
     Listen for cart:open events (e.g., after add-to-cart)
     ----------------------------------------------------------------------- */
  document.addEventListener('cart:open', function() {
    openDrawer();
  });

  /* -----------------------------------------------------------------------
     Helper: Format Money
     Uses Shopify money format with Euro. Prices are in cents.
     ----------------------------------------------------------------------- */
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    cents = parseInt(cents);

    var amount = (cents / 100).toFixed(2);
    /* German format: comma as decimal separator, dot as thousand separator */
    var parts = amount.split('.');
    var euros = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var formatted = euros + ',' + parts[1];

    return formatted + '\u00a0\u20ac';
  }

  /* -----------------------------------------------------------------------
     Helper: Get Sized Image URL
     Converts Shopify image URL to specific size
     ----------------------------------------------------------------------- */
  function getSizedImageUrl(url, size) {
    if (!url) return '';
    /* If URL already contains size parameter, replace it */
    if (url.indexOf('_') !== -1 && url.match(/_\d+x\d*/)) {
      return url.replace(/_\d+x\d*/, '_' + size);
    }
    /* Add size before file extension */
    var match = url.match(/(\.[^.]+)$/);
    if (match) {
      return url.replace(match[0], '_' + size + match[0]);
    }
    return url;
  }

  /* -----------------------------------------------------------------------
     Helper: Escape HTML
     ----------------------------------------------------------------------- */
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

})();
